import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { imageAssetsService, ImageAsset, ImageCategory } from '../../services/imageAssetsService';

interface ImagePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  selectedImage?: string;
  category: 'hardware' | 'software' | 'generic' | 'telco';
  title?: string;
  allowUpload?: boolean;
}

export function ImagePicker({ 
  isOpen, 
  onClose, 
  onSelectImage, 
  selectedImage, 
  category,
  title = 'Select Image',
  allowUpload = true
}: ImagePickerProps) {
  const [imageCategory, setImageCategory] = useState<ImageCategory | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAssets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load ALL categories to give users access to all images regardless of product domain
      // Note: 'telco' category name maps to 'telstra' subcategory from hardware in the backend
      const allCategories = ['hardware', 'software', 'generic', 'telco', 'custom'];
      const categoryDisplayNames: Record<string, string> = {
        hardware: 'Hardware',
        software: 'Software',
        generic: 'Generic/Consulting',
        telco: 'Telstra',
        custom: 'Custom Uploads'
      };
      
      const allSubcategories: { name: string; displayName: string; assets: ImageAsset[] }[] = [];
      
      // Load each category in parallel
      const categoryPromises = allCategories.map(async (cat) => {
        try {
          const data = await imageAssetsService.getAssetsByCategory(cat);
          if (data?.subcategories?.length) {
            return { category: cat, data };
          }
        } catch (e) {
          // Category not available
        }
        return null;
      });
      
      const results = await Promise.all(categoryPromises);
      
      // Process each category's results
      results.forEach((result) => {
        if (!result) return;
        const { category: cat, data } = result;
        
        // For each subcategory in this category, add it with a prefixed display name
        data.subcategories.forEach((sub) => {
          const displayPrefix = categoryDisplayNames[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
          // Skip if no assets
          if (!sub.assets || sub.assets.length === 0) return;
          
          // Create unique name for the subcategory
          const uniqueName = `${cat}/${sub.name}`;
          const displayName = cat === 'custom' 
            ? 'Custom Uploads' 
            : `${displayPrefix} - ${sub.displayName || sub.name}`;
          
          allSubcategories.push({
            name: uniqueName,
            displayName,
            assets: sub.assets
          });
        });
      });
      
      // Sort subcategories: Custom first, then by category name
      allSubcategories.sort((a, b) => {
        if (a.name.startsWith('custom/')) return -1;
        if (b.name.startsWith('custom/')) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
      
      if (allSubcategories.length > 0) {
        const mergedCategory: ImageCategory = {
          name: 'all',
          displayName: 'All Images',
          subcategories: allSubcategories
        };
        
        setImageCategory(mergedCategory);
        
        // Auto-select custom if available, otherwise first subcategory
        const customSubcat = allSubcategories.find(sub => sub.name.startsWith('custom/'));
        if (customSubcat && customSubcat.assets.length > 0) {
          setSelectedSubcategory(customSubcat.name);
        } else if (allSubcategories.length) {
          setSelectedSubcategory(allSubcategories[0].name);
        }
      } else {
        setError(`No images found. You can upload your own images.`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load images');
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load assets when category changes or modal opens
  useEffect(() => {
    if (isOpen) {
      loadAssets();
    } else {
      // Reset preview when modal closes
      setPreviewImage(null);
      setSearchTerm('');
      setError(null);
    }
  }, [isOpen, category, loadAssets]);

  // Handle keyboard events for closing preview
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (previewImage) {
          closePreview();
        } else if (isOpen) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, previewImage, onClose]);

  // Filter assets based on search and selected subcategory
  const filteredAssets = useMemo(() => {
    if (!imageCategory) return [];

    let allAssets: ImageAsset[] = [];
    
    if (selectedSubcategory) {
      const subcategory = imageCategory.subcategories.find(sub => sub.name === selectedSubcategory);
      allAssets = subcategory?.assets || [];
    } else {
      allAssets = imageCategory.subcategories.flatMap(sub => sub.assets);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      allAssets = allAssets.filter(asset => 
        asset.displayName.toLowerCase().includes(search) ||
        asset.filename.toLowerCase().includes(search) ||
        asset.description?.toLowerCase().includes(search)
      );
    }

    return allAssets;
  }, [imageCategory, selectedSubcategory, searchTerm]);

  const handleSelectImage = async (asset: ImageAsset) => {
    try {
      // Store as a pack: URL instead of the SAS URL
      // This allows the image to be resolved dynamically with fresh SAS tokens
      // Format: pack:{subcategory}/{filename}
      // The subcategory and filename uniquely identify the asset
      
      // Get the actual subcategory name (not the category/subcategory composite)
      // selectedSubcategory format is "category/subcategory" (e.g., "telco/telstra")
      // We want just the subcategory part for the pack URL
      let subcategory = asset.subcategory;
      if (!subcategory && selectedSubcategory) {
        // Extract the subcategory from the composite name (after the slash)
        const parts = selectedSubcategory.split('/');
        subcategory = parts.length > 1 ? parts[parts.length - 1] : parts[0];
      }
      subcategory = subcategory || 'general';
      
      const packUrl = `pack:${subcategory}/${asset.filename}`;
      
      onSelectImage(packUrl);
      onClose();
    } catch (err) {
      setError('Failed to select image. Please try again.');
    }
  };

  const handlePreviewImage = (asset: ImageAsset, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selection when clicking preview
    setPreviewImage(asset.url);
  };

  const closePreview = () => {
    setPreviewImage(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Resize image to max dimensions while maintaining aspect ratio
   */
  const resizeImage = (file: File, maxSize: number = 512): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        let { width, height } = img;

        // Calculate new dimensions maintaining aspect ratio
        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw with white background (for transparency)
        if (ctx) {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        }

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create image blob'));
            }
          },
          'image/png',
          0.9
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - PNG only
    if (file.type !== 'image/png') {
      setError('Only PNG images are allowed. Please select a .png file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (max 10MB before resize)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadSuccess(null);

    try {
      // Resize image to 512x512 max
      const resizedBlob = await resizeImage(file, 512);
      const resizedFile = new File([resizedBlob], file.name.replace(/\.[^.]+$/, '.png'), { 
        type: 'image/png' 
      });

      const result = await imageAssetsService.uploadCustomImage(resizedFile, {
        category: 'custom',
        subcategory: category
      });

      if (result.success && result.url) {
        setUploadSuccess(`Image uploaded and resized successfully! Click to use it.`);
        // Refresh the asset list to show the new image
        await loadAssets();
        // Don't auto-close - let user see the success message and find their image
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      data-image-picker-root
      className="fixed inset-0 bg-black/50 overflow-y-auto pointer-events-auto"
      style={{ zIndex: 1000 }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="relative top-4 mx-auto w-[1000px] max-w-[95vw] bg-white rounded-lg shadow-xl min-h-[600px]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">
              Choose an image from any category for your product
            </p>
          </div>
          <button 
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
            title="Close image picker"
            aria-label="Close image picker"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Success State */}
          {uploadSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-emerald-800">{uploadSuccess}</span>
                <button 
                  onClick={() => setUploadSuccess(null)} 
                  className="ml-auto text-emerald-600 hover:text-emerald-800"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800">{error}</span>
                <button 
                  onClick={() => setError(null)} 
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center justify-between mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition text-sm"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
            </div>

            {/* Subcategory Filter */}
            {imageCategory && imageCategory.subcategories.length > 1 && (
              <div className="ml-4">
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  aria-label="Filter by subcategory"
                >
                  <option value="">All Categories</option>
                  {imageCategory.subcategories.map(sub => (
                    <option key={sub.name} value={sub.name}>
                      {sub.displayName} ({sub.assets.length})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-2">
              {allowUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,.png"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUploadClick} 
                    disabled={isUploading}
                    className="border-primary-300 text-primary-700 hover:bg-primary-50"
                    title="Upload a PNG image (max 512x512, will be auto-resized)"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {isUploading ? 'Uploading...' : 'Upload PNG'}
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={loadAssets} disabled={isLoading}>
                <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M3 21v-5h5" />
                </svg>
                {isLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading images...</span>
            </div>
          ) : (
            <>
              {/* Results Info */}
              <div className="text-sm text-gray-600 mb-4">
                {filteredAssets.length} image{filteredAssets.length !== 1 ? 's' : ''} available
                {selectedSubcategory && imageCategory && (
                  <span> in {imageCategory.subcategories.find(sub => sub.name === selectedSubcategory)?.displayName}</span>
                )}
                {searchTerm && <span> matching "{searchTerm}"</span>}
              </div>

              {/* Image Grid */}
              {filteredAssets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4 mx-auto">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <path d="M21 15l-5-5L5 21"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-1">
                    {searchTerm ? 'No Matching Images' : 'No Images Found'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {searchTerm ? 'Try adjusting your search terms' : 'No images available in this category'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 max-h-[400px] overflow-y-auto">
                  {filteredAssets.map((asset, index) => (
                    <div
                      key={`${asset.filename}-${index}`}
                      className={`relative group cursor-pointer rounded-lg border-2 transition-all hover:shadow-lg ${
                        selectedImage === asset.url 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                      onClick={() => handleSelectImage(asset)}
                    >
                      {/* Image */}
                      <div className="aspect-square p-3">
                        <img
                          src={asset.thumbnailUrl || asset.url}
                          alt={asset.displayName}
                          className="w-full h-full object-contain rounded"
                          onError={(e) => {
                            // Fallback to a placeholder if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NEwyNiA0MEwyMCAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                      </div>
                      
                      {/* Preview Button */}
                      <button
                        className="absolute top-2 left-2 w-8 h-8 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => handlePreviewImage(asset, e)}
                        title="Preview image"
                        aria-label="Preview image"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>

                      {/* Label */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="font-medium truncate">{asset.displayName}</div>
                      </div>

                      {/* Filename below image */}
                      <div className="mt-2 text-center">
                        <div className="text-xs font-medium text-gray-900 truncate" title={asset.displayName}>
                          {asset.displayName}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-1" title={asset.filename}>
                          {asset.filename.split('/').pop()}
                        </div>
                      </div>

                      {/* Selected Indicator */}
                      {selectedImage === asset.url && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="text-sm text-gray-600">
            {selectedImage 
              ? 'Image selected. Click an image to change selection.' 
              : allowUpload 
                ? 'Click an image to select it, or upload your own PNG (auto-resized to 512×512).'
                : 'Click an image to select it.'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {selectedImage && (
              <Button 
                className="bg-primary-600 text-white hover:bg-primary-700"
                onClick={() => {
                  if (selectedImage) {
                    onSelectImage(selectedImage);
                    onClose();
                  }
                }}
              >
                Use Selected Image
              </Button>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        {previewImage && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
            style={{ zIndex: 1010 }}
            onClick={closePreview}
          >
            <div className="relative max-w-full max-h-full">
              {/* Close Button */}
              <button
                className="absolute -top-12 right-0 w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors"
                onClick={closePreview}
                title="Close preview"
                aria-label="Close preview"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              
              {/* Preview Image */}
              <img
                src={previewImage}
                alt="Preview"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Instructions */}
              <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-white/80 text-sm text-center">
                <p>Click outside image or press Escape to close</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
